import { useState } from "react";
import {
  useOutreachProspects,
  useOutreachStats,
  useCreateProspects,
  useDraftProspect,
  useUpdateProspect,
  useDeleteProspect,
  useApproveBatch,
  type OutreachProspect,
} from "@/hooks/use-queries";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Send,
  Sparkles,
  Copy,
  Check,
  CheckCheck,
  Trash2,
  ExternalLink,
  RefreshCw,
  Users,
} from "lucide-react";

const CHANNELS = ["x", "instagram", "whatsapp"];
const SEGMENTS = ["crypto", "builder", "investor", "press"];
const STATUS_FLOW = ["new", "drafted", "approved", "sent", "replied", "skipped"];

const STATUS_STYLE: Record<string, string> = {
  new: "bg-card text-muted-foreground border-divider",
  drafted: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  approved: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  sent: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  replied: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  skipped: "bg-red-500/10 text-red-400/70 border-red-500/15",
};

// Build a click-to-send link where the platform supports it.
function sendLink(p: OutreachProspect): string | null {
  if (p.channel === "whatsapp") {
    const digits = (p.handle ?? "").replace(/[^0-9]/g, "");
    if (digits) {
      const text = p.draft ? `?text=${encodeURIComponent(p.draft)}` : "";
      return `https://wa.me/${digits}${text}`;
    }
  }
  if (p.profileUrl) return p.profileUrl;
  if (p.channel === "x" && p.handle) {
    return `https://x.com/${p.handle.replace(/^@/, "")}`;
  }
  if (p.channel === "instagram" && p.handle) {
    return `https://instagram.com/${p.handle.replace(/^@/, "")}`;
  }
  return null;
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Card className="glass-panel">
      <CardContent className="py-3 px-4">
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-xl font-display font-bold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function ProspectRow({ p }: { p: OutreachProspect }) {
  const draftMut = useDraftProspect();
  const updateMut = useUpdateProspect();
  const deleteMut = useDeleteProspect();
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(p.draft ?? "");
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const handleDraft = async () => {
    setNote(null);
    const res = await draftMut.mutateAsync(p.id);
    if (res?.generated === false) {
      setNote("OpenClaw engine unavailable (OpenRouter cap) — top up to draft.");
    } else if (res?.draft) {
      setDraftText(res.draft);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(p.draft ?? draftText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const link = sendLink(p);

  return (
    <Card className="glass-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{p.name}</span>
              {p.handle && <span className="text-xs font-mono text-muted-foreground">{p.handle}</span>}
              <Badge className="text-[10px] bg-surface-subtle border-divider uppercase">{p.channel}</Badge>
              <Badge className="text-[10px] bg-surface-subtle border-divider">{p.segment}</Badge>
            </div>
            {p.context && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.context}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={p.status}
              onChange={(e) => updateMut.mutate({ id: p.id, status: e.target.value })}
              className={`text-xs font-mono rounded-md border px-2 py-1 bg-transparent ${STATUS_STYLE[p.status] ?? ""}`}
            >
              {STATUS_FLOW.map((s) => (
                <option key={s} value={s} className="bg-background text-foreground">{s}</option>
              ))}
            </select>
            <Button size="sm" variant="ghost" onClick={() => deleteMut.mutate(p.id)} title="Remove">
              <Trash2 className="h-3.5 w-3.5 text-red-400/70" />
            </Button>
          </div>
        </div>

        {/* Draft */}
        {editing ? (
          <div className="space-y-2">
            <Textarea value={draftText} onChange={(e) => setDraftText(e.target.value)} rows={3} className="text-sm" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => { updateMut.mutate({ id: p.id, draft: draftText }); setEditing(false); }}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setDraftText(p.draft ?? ""); setEditing(false); }}>Cancel</Button>
            </div>
          </div>
        ) : p.draft ? (
          <div
            className="rounded-lg border border-divider/60 bg-surface-subtle/60 p-3 text-sm whitespace-pre-wrap cursor-text"
            onClick={() => { setDraftText(p.draft ?? ""); setEditing(true); }}
          >
            {p.draft}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No draft yet — generate one with OpenClaw.</p>
        )}

        {note && <p className="text-xs text-amber-400">{note}</p>}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={handleDraft} disabled={draftMut.isPending}>
            <Sparkles className={`h-3.5 w-3.5 mr-1.5 ${draftMut.isPending ? "animate-pulse" : ""}`} />
            {p.draft ? "Redraft" : "Draft with OpenClaw"}
          </Button>
          {p.draft && (
            <Button size="sm" variant="ghost" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          )}
          {link && (
            <Button size="sm" variant="ghost" asChild>
              <a href={link} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open {p.channel === "whatsapp" ? "WhatsApp" : "profile"}
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Parse bulk paste: "name | handle | channel | segment | context" per line.
function parseBulk(text: string) {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const [name, handle, channel, segment, ...rest] = line.split("|").map((x) => x.trim());
      return {
        name,
        handle: handle || undefined,
        channel: channel || undefined,
        segment: segment || undefined,
        context: rest.join(" | ") || undefined,
      };
    })
    .filter((p) => p.name);
}

export default function Outreach() {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const { data: prospects, isLoading } = useOutreachProspects(
    statusFilter ? { status: statusFilter } : undefined,
  );
  const { data: stats } = useOutreachStats();
  const createMut = useCreateProspects();
  const approveBatch = useApproveBatch();
  const [bulk, setBulk] = useState("");
  const draftedCount = stats?.byStatus?.drafted ?? 0;

  const handleAdd = async () => {
    const parsed = parseBulk(bulk);
    if (!parsed.length) return;
    await createMut.mutateAsync(parsed);
    setBulk("");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-mono text-primary uppercase tracking-[0.2em] mb-1">Agent</h2>
          <h1 className="text-3xl font-display font-bold flex items-center gap-2">
            <Send className="h-7 w-7 text-primary" /> Outreach
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            OpenClaw auto drafts personalized messages per channel. Approve the batch in one tap, and the docked
            phone sends them on a schedule, human paced.
          </p>
        </div>
        <Button
          size="lg"
          onClick={() => approveBatch.mutate()}
          disabled={draftedCount === 0 || approveBatch.isPending}
          className="shrink-0"
        >
          <CheckCheck className="h-4 w-4 mr-2" />
          {approveBatch.isPending ? "Approving…" : `Approve all drafted (${draftedCount})`}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        <StatPill label="Total" value={stats?.total ?? 0} color="" />
        <StatPill label="New" value={stats?.byStatus?.new ?? 0} color="text-muted-foreground" />
        <StatPill label="Drafted" value={stats?.byStatus?.drafted ?? 0} color="text-blue-400" />
        <StatPill label="Approved" value={stats?.byStatus?.approved ?? 0} color="text-purple-400" />
        <StatPill label="Sent" value={stats?.byStatus?.sent ?? 0} color="text-amber-400" />
        <StatPill label="Replied" value={stats?.byStatus?.replied ?? 0} color="text-emerald-400" />
      </div>

      {/* Add prospects */}
      <Card className="glass-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider text-primary flex items-center gap-2">
            <Users className="h-4 w-4" /> Add prospects
          </CardTitle>
          <CardDescription className="text-xs">
            One per line: <code className="text-foreground">name | handle | channel | segment | context</code>
            {" "}— channel = {CHANNELS.join("/")}, segment = {SEGMENTS.join("/")} (defaults: x / builder).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder={"Jane Doe | @jane | x | builder | builds agent eval tooling\nAcme Capital | acme.vc | whatsapp | investor | seed fund, ai+crypto"}
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            rows={3}
            className="text-sm font-mono"
          />
          <Button onClick={handleAdd} disabled={!bulk.trim() || createMut.isPending}>
            <Send className="h-4 w-4 mr-2" />
            {createMut.isPending ? "Adding…" : "Add to pipeline"}
          </Button>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={statusFilter === "" ? "default" : "outline"} className="font-mono text-xs" onClick={() => setStatusFilter("")}>all</Button>
        {STATUS_FLOW.map((s) => (
          <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} className="font-mono text-xs" onClick={() => setStatusFilter(s)}>{s}</Button>
        ))}
        <RefreshCw className="h-3.5 w-3.5 text-muted-foreground/40 ml-1" />
      </div>

      {/* Pipeline */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl bg-surface-subtle" />)}
        </div>
      ) : (prospects ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">
          No prospects yet. Add some above, then let OpenClaw draft the messages.
        </p>
      ) : (
        <div className="space-y-3">
          {(prospects ?? []).map((p) => <ProspectRow key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}
